import type { MinutePerson } from '../actions/types'

/**
 * Sorumlu satırının **gösterim birimi**: ya tek bir kişi ya da bir isim listesi (115).
 *
 * `people` grubun bu kayıtta duran üyeleridir — listenin bugünkü hâli değil. Liste ile eklenen bir
 * kişi sonradan tek tek çıkarılabildiği için ikisi ayrışabilir (kullanıcı kararı 01.10.2026).
 */
export interface ResponsibleGroup {
  /** Listeden geliyorsa liste kimliği; tekil kişide null. */
  managerListId: number | null
  /** Satırda görünen metin: liste adı ya da kişinin adı. */
  label: string
  /** Üzerine gelinince gösterilecek kişiler (tekil kişide tek elemanlı). */
  people: MinutePerson[]
}

/**
 * Sorumluları **isim listesine göre gruplar** (kullanıcı isteği 01.10.2026): aynı listeden gelenler tek
 * bir liste adı altında toplanır, listesiz olanlar kendi başına kalır.
 *
 * <p><b>Sıra korunur:</b> grup, kendi ilk üyesinin geldiği yerde durur. Grupları başa/sona toplamak
 * sorumlu sırasını kullanıcının girdiğinden farklı gösterirdi.
 *
 * <p><b>Adı çözülemeyen liste tekil gösterilir:</b> `managerListName` boşsa (sunucu adı çözememişse)
 * kişiler gruplanmaz. Alternatif, kullanıcıya boş başlıklı bir grup göstermekti — kimin sorumlu
 * olduğunu gizlerdi.
 */
export function groupResponsibles(responsibles: readonly MinutePerson[]): ResponsibleGroup[] {
  const groups: ResponsibleGroup[] = []
  const byList = new Map<number, ResponsibleGroup>()

  for (const person of responsibles) {
    const listId = person.managerListId ?? null
    const listName = person.managerListName ?? null
    if (listId == null || !listName) {
      groups.push({
        managerListId: null,
        label: person.fullName || person.employeeId,
        people: [person],
      })
      continue
    }
    const existing = byList.get(listId)
    if (existing) {
      existing.people.push(person)
      continue
    }
    const group: ResponsibleGroup = { managerListId: listId, label: listName, people: [person] }
    byList.set(listId, group)
    groups.push(group)
  }
  return groups
}

/**
 * Dar ekran (kart) özeti: liste adı **ve üyeleri** — `Üst Yönetim (Ayşe Yılmaz, Bora Kaya)`.
 *
 * <p><b>Üyeler burada açıkça yazılır</b> çünkü dokunmatik ekranda "üzerine gelme" diye bir şey
 * yoktur: yalnız liste adını göstermek, telefonda kimin sorumlu olduğunu <b>tümüyle erişilemez</b>
 * kılardı — önceki davranış tüm adları veriyordu, bu bir gerileme olurdu (push kapısı bulgusu
 * 01.10.2026). Tekil kişide parantez yoktur.
 */
export function groupLabels(responsibles: readonly MinutePerson[]): string {
  return groupResponsibles(responsibles)
    .map((g) => (g.managerListId == null ? g.label : `${g.label} (${groupTooltip(g)})`))
    .join(', ')
}

/** Bir grubun üzerine gelindiğinde gösterilecek kişi adları. */
export function groupTooltip(group: ResponsibleGroup): string {
  return group.people.map((p) => p.fullName || p.employeeId).join(', ')
}
